/*
# ECS's First Finite State Machine

This is an INTENTIONALLY SIMPLE implementation of a Finite State Machine.

It:
  - Supports only a few types of state: string, number, and symbol (because Javascript). No Objects!
  - Has no events, save a next() method. To check the state either use fsm.current, or fsm.is().
  - Is incredibly small api. Only three methods.
  - Is Synchronous

## But WHY!?!?

State Machines are very cool, but it's not yet understood how they are best handled in a pattern like
ECS, where state and behavior are dogmatically separated. This class exists to keep things simple,
hopefully maintainable, and with a minimum of footguns while we explore using State Machines and ECS together.

This is not a replacement for libraries like XState. If you find that you need more functionality, then best use
another option.

Example: 

```typescript

// define your states
type States = 'initial' | 'loading' | 'loaded';

// define the transitions between states
// The keys of the object are the current state of the state machine.
// The method that matches up to the current state is called when next() is called.
// The transition methods must always return the next state of the machine.
const transitions = {
  initial: () => 'loading',
  loading: () => 'loaded',
}

const initialState = 'initial';
const fsm = new SimpleFSM<States>(initialState, transitions);

fsm.next() // calls transitions.initial;

expect(fsm.is('loading')).to.equal(true);

fsm.next() // calls transitions.loading;

expect(fsm.is('loaded')).to.equal(true);

fsm.reset() // go back to the given initial state.

```
*/

type State = string | number | symbol;

export type Transitions<S extends State, D> = Record<
  S,
  (data: D | undefined, current: S) => S
>;

export default class SimpleFSM<S extends State, D = undefined> {
  current: S;

  inital: S;

  transitions: Transitions<S, D>;

  constructor(initialState: S, transitions: Transitions<S, D>) {
    this.inital = initialState;
    this.current = initialState;
    this.transitions = transitions;
  }

  next(data?: D): void {
    if (this.transitions[this.current]) {
      this.current = this.transitions[this.current](data, this.current);
    }
  }

  reset(): void {
    this.current = this.inital;
  }

  is(checkState: S): boolean {
    return this.current === checkState;
  }
}
